import { jsPDF } from "jspdf";
import { T } from "@/lib/i18n";
import { CARD_UI } from "@/lib/shareCards";

export const SIZES = { square: [1080, 1080], story: [1080, 1920] };
const SERIF = "'Cormorant Garamond', serif";
const SANS = "'Plus Jakarta Sans', sans-serif";

let sealP;
const seal = () => (sealP ||= new Promise((res) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = "/brand/seal.jpg"; }));

const wrap = (x, text, maxW) => {
  const out = [];
  let line = "";
  text.split(" ").forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (x.measureText(t).width > maxW && line) { out.push(line); line = w; } else line = t;
  });
  out.push(line);
  return out;
};

const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// card: { kicker, title, body?, points? }
export const drawCard = async (card, palette, size, lang) => {
  const [W, H] = SIZES[size];
  const tall = size === "story";
  const ui = CARD_UI[lang];
  await Promise.all(["600 40px 'Cormorant Garamond'", "400 20px 'Plus Jakarta Sans'", "600 20px 'Plus Jakarta Sans'", "700 20px 'Plus Jakarta Sans'"].map((f) => document.fonts?.load(f).catch(() => null)));
  const img = await seal();
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  const { bg, accent } = palette;

  x.fillStyle = bg; x.fillRect(0, 0, W, H);
  const g = x.createRadialGradient(W * 0.88, H * 0.08, 10, W * 0.88, H * 0.08, W * 0.9);
  g.addColorStop(0, hexA(accent, 0.26)); g.addColorStop(1, hexA(accent, 0));
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  x.strokeStyle = hexA(accent, 0.7); x.lineWidth = 3; x.strokeRect(40, 40, W - 80, H - 80);

  const P = 100;
  const maxW = W - P * 2;
  let y = tall ? 250 : 150;
  x.fillStyle = accent; x.font = `600 28px ${SANS}`;
  x.fillText(card.kicker.toUpperCase(), P, y);
  x.fillRect(P, y + 22, 70, 3);
  y += tall ? 170 : 120;

  if (card.points) {
    x.fillStyle = "#FFFFFF"; x.font = `600 ${tall ? 100 : 74}px ${SERIF}`;
    wrap(x, card.title, maxW).forEach((l) => { x.fillText(l, P, y); y += tall ? 104 : 78; });
    y += tall ? 80 : 26;
    card.points.forEach(([b, t]) => {
      x.fillStyle = accent; x.fillRect(P, y - 32, 6, tall ? 92 : 72);
      x.font = `700 ${tall ? 44 : 36}px ${SANS}`; x.fillStyle = "#F3E6C4"; x.fillText(b, P + 30, y);
      x.font = `400 ${tall ? 34 : 28}px ${SANS}`; x.fillStyle = "#DCE3F0";
      const ls = wrap(x, t, maxW - 30);
      ls.forEach((l, i) => x.fillText(l, P + 30, y + (tall ? 48 : 40) * (i + 1)));
      y += (tall ? 48 : 40) * (ls.length + 1) + (tall ? 80 : 16);
    });
  } else {
    const ts = tall ? 118 : 96;
    x.font = `600 ${ts}px ${SERIF}`;
    const tl = wrap(x, card.title, maxW);
    const bodyFont = `400 ${tall ? 44 : 38}px ${SANS}`;
    x.font = bodyFont;
    const bl = wrap(x, card.body, maxW);
    const block = tl.length * ts * 1.02 + 60 + bl.length * (tall ? 62 : 54);
    const top = tall ? 560 : 300;
    const bottomLimit = H - (tall ? 520 : 330);
    y = Math.max(top, top + (bottomLimit - top - block) / 2) + ts * 0.8;
    x.fillStyle = "#FFFFFF"; x.font = `600 ${ts}px ${SERIF}`;
    tl.forEach((l) => { x.fillText(l, P, y); y += ts * 1.02; });
    y += 30;
    x.fillStyle = accent; x.fillRect(P, y - 20, 90, 3);
    y += 40;
    x.fillStyle = "#E6EAF2"; x.font = bodyFont;
    bl.forEach((l) => { x.fillText(l, P, y); y += tall ? 62 : 54; });
  }

  const fy = H - (tall ? 300 : 210);
  x.strokeStyle = hexA(accent, 0.35); x.lineWidth = 1.5;
  x.beginPath(); x.moveTo(P, fy); x.lineTo(W - P, fy); x.stroke();
  if (img) x.drawImage(img, P, fy + 26, 56, 56);
  x.fillStyle = "#F3E6C4"; x.font = `600 26px ${SANS}`;
  x.fillText("Bright Stories Network", P + 74, fy + 50);
  x.fillStyle = hexA("#F3E6C4", 0.7); x.font = `400 22px ${SANS}`;
  x.fillText(ui.url, P + 74, fy + 80);
  x.textAlign = "right"; x.fillStyle = accent; x.font = `600 22px ${SANS}`;
  x.fillText(ui.help, W - P, fy + 64);
  x.textAlign = "center"; x.fillStyle = "rgba(220,227,240,0.62)"; x.font = `italic 20px ${SANS}`;
  wrap(x, T[lang].disclaimer, W - 240).forEach((l, i) => x.fillText(l, W / 2, fy + 128 + i * 26));
  x.textAlign = "left";
  return cv;
};

export const toBlob = (cv) => new Promise((res) => cv.toBlob(res, "image/png"));

export const printSet = async (cards, palette, lang, title, file) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const s = 250, gx = (W - s * 2) / 3, top = 70;
  for (let i = 0; i < cards.length; i++) {
    if (i % 4 === 0) {
      if (i) doc.addPage();
      doc.setFont("times", "bold"); doc.setFontSize(16); doc.setTextColor(11, 23, 48);
      doc.text(`Bright Stories Network  ·  ${title}`, W / 2, 46, { align: "center" });
      doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(60, 70, 90);
      doc.text(T[lang].disclaimer, W / 2, H - 30, { align: "center" });
      doc.setDrawColor(190, 190, 190); doc.setLineDashPattern([4, 4], 0);
      doc.line(W / 2, top - 10, W / 2, top + s * 2 + 40); doc.line(20, top + s + 15, W - 20, top + s + 15);
      doc.setLineDashPattern([], 0);
    }
    const k = i % 4;
    const cv = await drawCard(cards[i], palette, "square", lang);
    doc.addImage(cv.toDataURL("image/jpeg", 0.9), "JPEG", gx + (k % 2) * (s + gx), top + Math.floor(k / 2) * (s + 30), s, s);
  }
  doc.save(file);
};
